import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from './api';

interface ApiQuery<T> {
  data: T | null;
  error: string | null;
  errorCode: string | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Small data-fetching hook: GET on mount + manual reload. Pass null to skip.
 * `actingFor` (optional) is sent as X-Acting-For — the employer-mandate
 * endpoints (grievance employer inbox, an employer's own ratio) key on it.
 * Kept as a plain string param (not a generic options bag) so it's trivial
 * to include in the effect's dependency array.
 */
export function useApiQuery<T>(path: string | null, actingFor?: string): ApiQuery<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(path));
  const [tick, setTick] = useState(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    if (!path) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setErrorCode(null);
    api
      .get<T>(path, actingFor ? { actingFor } : undefined)
      .then((d) => {
        if (alive.current) setData(d);
      })
      .catch((e: unknown) => {
        if (!alive.current) return;
        setError(e instanceof ApiError ? e.message : 'Request failed');
        setErrorCode(e instanceof ApiError ? e.code : null);
      })
      .finally(() => {
        if (alive.current) setLoading(false);
      });
    return () => {
      alive.current = false;
    };
  }, [path, actingFor, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, errorCode, loading, reload };
}
